import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Eye, EyeOff, Info } from "lucide-react";

import { TextInputType } from "@utils/types/components";
import { ResourcesKey } from "@modules/localizations/translate";

import TranslatedText from "../display/TranslatedText";
import InputLabel from "../display/InputLabel";

const TextInput = React.forwardRef<HTMLInputElement, TextInputType>(
  (
    {
      id,
      label,
      placeholder = "",
      disabled,
      required = false,
      readOnly,
      shape = "squre",
      textColorClassName = "text-black",
      bgColorClassName = "bg-white",
      value,
      onChange,
      onEnter,
      inputHtmlType = "text",
      prefix,
      suffix,
      inputExtraClassNames,
      isError,
      fieldState,
      onBlur,
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(e.target.value);
    };

    const { t } = useTranslation();

    const onPasswordToggle = () => {
      if (value && inputHtmlType === "password") {
        setShowPassword((showPassword) => !showPassword);
      }
    };

    return (
      <div className={`w-full ${isError && "mb-3"}`}>
        {label && (
          <InputLabel
            name={id ?? ""}
            text={t(`inputs.labels.${label}`, label.toString() || "") as string}
            required={required}
          />
        )}
        <div className={`relative`}>
          {prefix && (
            <div
              className={`pointer-events-none absolute inset-y-0 flex items-center ps-3 ltr:left-0 rtl:right-0`}
            >
              <span className="text-gray-500 sm:text-sm">{prefix}</span>
            </div>
          )}
          <input
            ref={ref}
            id={id}
            type={
              inputHtmlType !== "password"
                ? inputHtmlType
                : showPassword
                  ? "text"
                  : "password"
            }
            disabled={disabled}
            autoComplete={readOnly ? "off" : "on"}
            readOnly={readOnly}
            required={required}
            value={value || ""}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={(event) => {
              if (event.code === "Enter") {
                event.preventDefault();

                onEnter && value && onEnter(value);
              }
            }}
            className={`w-full border border-gray-300 py-1 focus:outline-none sm:text-sm lg:py-2 ${
              value != undefined && value != ""
                ? isError
                  ? "border-[#FB5D63] outline-none ring-1 ring-[#FB5D63]"
                  : "border-[#34CC5A] outline-none ring-1 ring-[#34CC5A]"
                : ""
            } ${shape === "circle" ? "rounded-full" : "rounded-md"} ${
              prefix && "ltr:pl-9 rtl:pr-9"
            } ${
              suffix && "ltr:pr-9 rtl:pl-9"
            } ${bgColorClassName} ${textColorClassName} ${inputExtraClassNames}`}
            placeholder={`${t(
              `inputs.placeholders.${placeholder}`,
              placeholder || ""
            )}${!label && required ? "*" : ""}`}
          />
          <div
            className={`absolute inset-y-0 mt-1 flex cursor-pointer items-center ltr:right-1 ltr:mr-1 rtl:left-1 rtl:ml-1`}
            onClick={() => {
              onPasswordToggle();
            }}
          >
            {inputHtmlType !== "password" ? (
              suffix
            ) : value ? (
              showPassword ? (
                <EyeOff color="gray" size={25} />
              ) : (
                <Eye color="gray" size={25} />
              )
            ) : null}
          </div>
          {inputHtmlType === "email" && value !== undefined && value !== "" ? (
            <div
              className={`absolute inset-y-0 flex items-center px-2 ltr:right-0 rtl:left-0`}
            >
              {isError ? (
                <Info className="text-[#FB5D63]" />
              ) : (
                <CheckCircle2 className="text-[#34CC5A]" />
              )}
            </div>
          ) : null}
          {isError && (
            <div className="absolute top-[110%] text-[10px] ltr:left-1 rtl:right-1">
              <TranslatedText
                text={
                  `onboarding.inputs.errors.${fieldState?.error?.message}` as ResourcesKey
                }
                className="!text-[#FB5D63]"
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

TextInput.displayName = "TextInput";

export default TextInput;
